import React, { useContext } from 'react'
import { ReactReduxContext } from 'react-redux';
import clsx from 'clsx'
import PropTypes from 'prop-types';
import useMenuKeyboardNav from '../../hooks/useMenuKeyboardNav';
import { Localize as AriaLocalize } from '../../shared/ariaLabelLocalize';

export const CountryStateDropDown = ({ selectedCountryCode, selectedCountryName, noCountrySelectedText, formik, ...otherProps }) => {
    const { triggerRef: countryTriggerRef, menuRef: countryMenuRef } = useMenuKeyboardNav({
        itemSelector: '.available-country-btn',
    });
    const { triggerRef: stateTriggerRef, menuRef: stateMenuRef } = useMenuKeyboardNav({
        itemSelector: '.available-country-btn',
    });
    const reduxContext = useContext(ReactReduxContext);
    const ariaLangId = reduxContext?.store?.getState()?.language?.selectedlanguage?.id;
    const ariaLocalised = AriaLocalize[ariaLangId] || AriaLocalize['en_US'];

    return (
        <div className="row">
            <div className="col-lg-6 col-md-6">
                <div className="form-group">
                    <div className="dropdown dropdown-box country-selection">
                        <button
                            className="btn btn-secondary dropdown-toggle dropdown-toggle-btn triangle-none"
                            type="button"
                            onClick={() => otherProps.handleSetSelectCountry(formik.setFieldValue)} id="country-selection"
                            data-testid="country-selection"
                            data-bs-toggle="dropdown"
                            aria-haspopup="listbox"
                            aria-expanded="false"
                            aria-label={ariaLocalised.country_currently.replace('{value}', selectedCountryCode ? selectedCountryName : noCountrySelectedText)}
                            ref={countryTriggerRef}>
                            {selectedCountryCode && <span className={`for-flag ${selectedCountryCode.toLowerCase()}`} aria-hidden="true"></span>}
                            <span className="for-text">{selectedCountryCode ? selectedCountryName : noCountrySelectedText}</span>
                            <span className="dropdown-toggle-arrow icon-down-triangle-round" aria-hidden="true"></span>
                        </button>
                        <div className="dropdown-menu dropdown-menu-list" aria-labelledby="country-selection" ref={countryMenuRef}>
                            <div className="dropdown-menu-in">
                                <div className="search-form sm">
                                    <div className="form-group">
                                        <span className="for-icon icon-search"></span>
                                        <input
                                            id="countryNameSearchId"
                                            data-testid="countryNameSearchId"
                                            type="text"
                                            className="form-control form-control-grey"
                                            placeholder='Search'
                                            aria-label={ariaLocalised.search_countries}
                                            value={otherProps.countrySearchValue}
                                            onChange={e => otherProps.setCountrySearchValue(e.target.value)}
                                        />
                                    </div>
                                </div>
                                {/* WCAG 2.1.1 (W-15 axe rule scrollable-region-focusable):
                                    scrollable wrapper must be keyboard-focusable so Safari users
                                    can scroll it with arrow keys. tabIndex on a non-interactive
                                    scroll wrapper is the exact remediation axe prescribes. */}
                                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                                <div className="country-selection-list custom-scroll-div-thin" tabIndex={0}>
                                    <ul role="listbox" aria-labelledby="country-selection">
                                        {!otherProps.countrySearchValue && <li role="presentation">
                                            <div className="no-search-found">
                                                {noCountrySelectedText}
                                            </div>
                                        </li>}
                                        {otherProps.filteredCountryBasedOnSearch.map((data, index) => {
                                            const isSelected = selectedCountryCode === data.CODE;
                                            return (
                                                <li key={index} role="presentation">
                                                    <button className='btn available-country-btn' type='button' onClick={() => otherProps.handleCountryChanged(data.CODE)}
                                                        role="option" aria-selected={isSelected} tabIndex={-1} data-typeahead={data.NAME}>
                                                        <span className={clsx('for-flag', data?.CODE?.toLowerCase())} aria-hidden="true"></span>
                                                        <span className="for-text">{data.NAME}</span>
                                                    </button>
                                                </li>
                                            )
                                        })}
                                        {(otherProps.filteredCountryBasedOnSearch.length < 1 && otherProps.countrySearchValue !== '') && <li role="presentation">
                                            <div className="no-search-found">
                                                <strong>{otherProps.noSearchFound}</strong>
                                            </div>
                                        </li>}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-lg-6 col-md-6">
                <div className="form-group">
                    <div className="dropdown dropdown-box">
                        <button
                            className="btn btn-secondary dropdown-toggle dropdown-toggle-btn triangle-none"
                            type="button"
                            onClick={() => otherProps.handleSetSelectState(formik.setFieldValue)}
                            id="state-selection"
                            data-bs-toggle="dropdown"
                            aria-haspopup="listbox"
                            aria-expanded="false"
                            aria-label={ariaLocalised.state_currently.replace('{value}', otherProps.selectedStateName)}
                            ref={stateTriggerRef}>
                            <span className="for-text">{otherProps.selectedStateName}</span>
                            <span className="dropdown-toggle-arrow icon-down-triangle-round" aria-hidden="true"></span>
                        </button>
                        <div className="dropdown-menu dropdown-menu-list" aria-labelledby="state-selection" ref={stateMenuRef}>
                            <div className="dropdown-menu-in">
                                <div className="search-form sm">
                                    <div className="form-group">
                                        <span className="for-icon icon-search"></span>
                                        {/* autoComplete="off" suppresses the browser's
                                            address-level1 (state) autofill overlay, which was
                                            hijacking ArrowDown from the listbox below. */}
                                        <input
                                            id="stateNameSearchId"
                                            data-testid="stateNameSearchId"
                                            type="text"
                                            className="form-control form-control-grey"
                                            placeholder='Search'
                                            aria-label={ariaLocalised.search_states}
                                            value={otherProps.stateSearchValue}
                                            onChange={e => otherProps.setStateSearchValue(e.target.value)}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>
                                {/* WCAG 2.1.1 (W-15 axe rule scrollable-region-focusable):
                                    scrollable wrapper must be keyboard-focusable so Safari users
                                    can scroll it with arrow keys. tabIndex on a non-interactive
                                    scroll wrapper is the exact remediation axe prescribes. */}
                                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                                <div className="country-selection-list custom-scroll-div-thin" tabIndex={0}>
                                    <ul role="listbox" aria-labelledby="state-selection">
                                        {!otherProps.stateSearchValue &&
                                            <li role="presentation">
                                                <div className="no-search-found">
                                                    {otherProps.noStateSelectedText}
                                                </div>
                                            </li>
                                        }
                                        {otherProps.filteredStateBasedOnSearch?.map((data, index) => {
                                            const isSelected = otherProps.selectedStateName === data;
                                            return (
                                                <li key={index} role="presentation">
                                                    <button className='btn available-country-btn' type='button' onClick={() => otherProps.setSelectedStateName(data)}
                                                        role="option" aria-selected={isSelected} tabIndex={-1} data-typeahead={data}>
                                                        <span className="for-text">{data}</span>
                                                    </button>
                                                </li>
                                            )
                                        })
                                        }
                                        {(otherProps.filteredStateBasedOnSearch.length < 1 && otherProps.stateSearchValue !== '') &&
                                            <li role="presentation">
                                                <div className="no-search-found">
                                                    <strong> {otherProps.noSearchFound} </strong>
                                                </div>
                                            </li>
                                        }
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

CountryStateDropDown.propTypes = {
    selectedCountryCode: PropTypes.string,
    selectedCountryName: PropTypes.string,
    noCountrySelectedText: PropTypes.string,
    formik: PropTypes.object,
}